import {
    CSSProperties,
    MouseEventHandler,
    PropsWithChildren,
    useContext,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';

import { TimelineContext, TimelineItemData } from './Timeline';

import { assert } from '../../utils/assert';

import styles from './TimelineItem.module.scss';

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const REFERENCE_TIMEZONES = [
    { label: 'San Francisco, US', timeZone: 'America/Los_Angeles' },
    { label: 'Brisbane, AU', timeZone: 'Australia/Brisbane' },
] as const;

function formatInTimezone(date: Date, timeZone: string): string {
    const datePart = new Intl.DateTimeFormat(undefined, {
        timeZone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date);

    let offsetRaw = '';

    try {
        offsetRaw =
            new Intl.DateTimeFormat(undefined, {
                timeZone,
                timeZoneName: 'shortOffset',
            })
                .formatToParts(date)
                .find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
        // Some runtimes (e.g. older Safari) don't support `shortOffset`.
    }

    // "GMT+1" → "UTC +1", "GMT-7" → "UTC -7"
    const utcOffset = offsetRaw.replace(/^GMT/, 'UTC').replace(/([+-])/, ' $1');

    return utcOffset ? `${datePart} (${utcOffset})` : datePart;
}

function TimelineTooltip({
    date,
    label,
}: {
    date: Date;
    label: 'Starts' | 'Ends';
}) {
    return (
        <>
            <span className={styles.tooltipTitle}>
                {label}: {formatInTimezone(date, LOCAL_TIMEZONE)}
            </span>
            {REFERENCE_TIMEZONES.map(({ label: tzLabel, timeZone }) => (
                <span key={timeZone} className={styles.tooltipRef}>
                    {tzLabel}: {formatInTimezone(date, timeZone)}
                </span>
            ))}
        </>
    );
}

interface TimelineItemProps extends PropsWithChildren {
    style?: CSSProperties;
    className?: string;
    value: TimelineItemData;
    handleColor?: string;
    onMove?: (value: TimelineItemData) => void;
    onStartMove?: (value: Date) => void;
    onStartExtend?: () => void;
    onEndMove?: (value: Date) => void;
    onEndExtend?: () => void;
    onContextMenu?: MouseEventHandler;
}

const GAP = 4;
export const TimelineItem = ({
    children,
    style = {},
    className,
    value,
    handleColor,
    onMove,
    onStartMove,
    onEndMove,
    onEndExtend,
    onStartExtend,
    onContextMenu,
}: TimelineItemProps) => {
    const context = useContext(TimelineContext);
    assert(context, 'Must be used within Timeline compoennt');

    const { start, end, lane } = value;

    const {
        isVisible,
        upperBound,
        positionToLane,
        laneToPosition,
        laneHeight,
        timeToPosition,
        pixelsPerMs,
    } = context;

    const left = timeToPosition(start.getTime());
    const top = laneToPosition(lane) + GAP;
    const right = Math.max(
        end ? timeToPosition(end.getTime()) : timeToPosition(upperBound),
        left,
    );

    const [isGrabbing, setIsGrabbing] = useState<boolean>(false);
    const startPointerDownRef = useRef<{ mouseX: number; time: number } | null>(
        null,
    );
    const endPointerDownRef = useRef<{ mouseX: number; time: number } | null>(
        null,
    );

    if (!isVisible(start.getTime(), end ? end.getTime() : upperBound)) {
        return null;
    }

    return (
        <>
            <div
                style={{
                    height: laneHeight - GAP * 2,
                    top,
                    left,
                    width: right - left,
                    ...style,
                }}
                className={classNames(className, styles.container, {
                    [styles.grabbing]: isGrabbing,
                })}
                onContextMenu={onContextMenu}
                onPointerDown={(e) => {
                    if (e.button !== 0) {
                        // Only trigger on left mouse button
                        return;
                    }

                    if (e.shiftKey && !value.end) {
                        const deltaMs = e.nativeEvent.offsetX / pixelsPerMs;
                        onEndMove?.(new Date(value.start.getTime() + deltaMs));
                    } else {
                        setIsGrabbing(true);
                        e.currentTarget.setPointerCapture(e.pointerId);
                        startPointerDownRef.current = {
                            mouseX: e.clientX,
                            time: start.getTime(),
                        };
                        if (end) {
                            endPointerDownRef.current = {
                                mouseX: e.clientX,
                                time: end.getTime(),
                            };
                        }
                    }
                }}
                onPointerMove={(e) => {
                    if (isGrabbing && startPointerDownRef.current) {
                        const delta =
                            e.clientX - startPointerDownRef.current.mouseX;
                        const deltaMs = delta / pixelsPerMs;
                        const newLane = positionToLane(
                            e.nativeEvent.offsetY + laneHeight * lane,
                        );
                        const newStart = new Date(
                            startPointerDownRef.current.time + deltaMs,
                        );
                        const defaultEnd = new Date(
                            newStart.getTime() + 24 * 60 * 60 * 1000,
                        );
                        const newEnd = endPointerDownRef.current
                            ? new Date(endPointerDownRef.current.time + deltaMs)
                            : end
                              ? new Date(end.getTime() + deltaMs)
                              : defaultEnd;
                        onMove?.({
                            lane: newLane,
                            start: newStart,
                            end: newEnd,
                        });
                    } else if (startPointerDownRef.current !== null) {
                        const delta =
                            e.clientX - startPointerDownRef.current.mouseX;
                        const deltaMs = delta / pixelsPerMs;

                        onStartMove?.(
                            new Date(
                                startPointerDownRef.current.time + deltaMs,
                            ),
                        );
                    } else if (endPointerDownRef.current !== null) {
                        const delta =
                            e.clientX - endPointerDownRef.current.mouseX;
                        const deltaMs = delta / pixelsPerMs;

                        onEndMove?.(
                            new Date(endPointerDownRef.current.time + deltaMs),
                        );
                    }
                }}
                onPointerUp={(e) => {
                    setIsGrabbing(false);
                    e.currentTarget.releasePointerCapture(e.pointerId);
                    startPointerDownRef.current = null;
                    endPointerDownRef.current = null;
                }}
            >
                <div
                    style={{ background: handleColor }}
                    className={classNames(styles.grabHandle, styles.left)}
                    onPointerDown={(e) => {
                        if (e.button !== 0) {
                            // Only trigger on left mouse button
                            return;
                        }

                        e.stopPropagation();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        startPointerDownRef.current = {
                            mouseX: e.clientX,
                            time: start.getTime(),
                        };
                    }}
                    onDoubleClick={() => {
                        onStartExtend?.();
                    }}
                />
                {children}

                {end && (
                    <div
                        style={{ background: handleColor }}
                        className={classNames(styles.grabHandle, styles.right)}
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            e.currentTarget.setPointerCapture(e.pointerId);
                            endPointerDownRef.current = {
                                mouseX: e.clientX,
                                time: end.getTime(),
                            };
                        }}
                        onDoubleClick={() => {
                            onEndExtend?.();
                        }}
                    />
                )}

                <div
                    className={classNames(
                        styles.timeTooltip,
                        styles.timeTooltipStart,
                        lane === 0 && styles.timeTooltipBelow,
                    )}
                >
                    <TimelineTooltip date={value.start} label="Starts" />
                </div>
                {value.end && (
                    <div
                        className={classNames(
                            styles.timeTooltip,
                            styles.timeTooltipEnd,
                            lane === 0 && styles.timeTooltipBelow,
                        )}
                    >
                        <TimelineTooltip date={value.end} label="Ends" />
                    </div>
                )}
            </div>
        </>
    );
};
